import AddIcon from '@mui/icons-material/Add';
import CommentIcon from '@mui/icons-material/Comment';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import InfoIcon from '@mui/icons-material/Info';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardMedia from '@mui/material/CardMedia';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import React, { Suspense, useEffect, useState, type JSX } from 'react';
import { aktionErlaubt } from '../domain/aktionErlaubt';
import { isAenderbar } from '../domain/isAenderbar';
import { AKTION_FOTO_ERFASSEN } from '../frontendConstants';
import { useDateiUrl } from '../hooks/useDateiUrl';
import { useUser } from '../hooks/useUser';
import { Feld, TexterkennungKategorie } from '../shared/constants';
import { toDateTimeString } from '../shared/dateTime';
import { FOTOKATEGORIEN } from '../shared/domain/vorgang';
import { Datei, Foto as FotoType, Person, Vorgang } from '../types';
import './Foto.css';
import { FotoKategorienEditierenDialog } from './dialog/FotoKategorienEditierenDialog';
import { FotoLoeschenBestaetigungsdialog } from './dialog/FotoLoeschenBestaetigungsdialog';
import InfoDialog from './dialog/InfoDialog';
import { TexterkennungModalProps } from './modal/TexterkennungModal';

const TexterkennungModal = React.lazy(() => import('./modal/TexterkennungModal'));

function LazyTexterkennungModal(props: TexterkennungModalProps) {
  return (
    <Suspense fallback={'Lade...'}>
      <TexterkennungModal {...props} />
    </Suspense>
  );
}

export type Props = {
  readonly fotoIndex: number;
  readonly vorgang: Vorgang;
  readonly foto: FotoType;
  readonly blob: string;
  readonly onDelete: (value: FotoType[]) => void;
  readonly handleKategorienUmschalten: (foto: FotoType, kategorien: string[]) => void;
  readonly isLoading: boolean;
  readonly setFahrzeughalter: (person: Person) => void;
  readonly fahrzeughalter: Person;
  readonly setShowPersonEingabeMaske: (value: boolean) => void;
  readonly showKategorieAuswahl: boolean;
  readonly setBildbearbeitenModalOpen: (value: number) => void;
  readonly showBeschreibungEdit: boolean;
  readonly editBeschreibung: (foto: FotoType, value: string) => void;
  readonly speichereVorgang: (aenderungen?: Partial<Vorgang>) => void;
};

export function Foto({
  fotoIndex,
  vorgang,
  foto,
  blob,
  onDelete,
  handleKategorienUmschalten,
  isLoading,
  setFahrzeughalter,
  fahrzeughalter,
  setShowPersonEingabeMaske,
  showKategorieAuswahl,
  setBildbearbeitenModalOpen,
  showBeschreibungEdit,
  editBeschreibung,
  speichereVorgang
}: Props): JSX.Element {
  const { gruppenVonMandant } = useUser();
  const url = useDateiUrl(foto.key);

  const [anchorEl, setAnchorEl] = useState<(EventTarget & HTMLButtonElement) | null>(null);
  const [fotoLoeschenBestaetigungsdialogOpen, setFotoLoeschenBestaetigungsdialogOpen] = useState(false);
  const [texterkennungModalOpen, setTexterkennungModalOpen] = useState(false);
  const [kategorieAuswahl, setKategorieAuswahl] = useState(showKategorieAuswahl || false);
  const [dateiInfoDialogOpen, setDateiInfoDialogOpen] = useState(false);
  const [kategorienEditierenDialogOpen, setKategorienEditierenDialogOpen] = useState(false);

  const isDisabled = isLoading || !isAenderbar(vorgang) || !aktionErlaubt(AKTION_FOTO_ERFASSEN, gruppenVonMandant(vorgang.mandant), vorgang.status);

  useEffect(() => {
    setKategorieAuswahl(showKategorieAuswahl);
  }, [showKategorieAuswahl]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenFotoLoeschenBestaetigungsdialog = () => {
    setFotoLoeschenBestaetigungsdialogOpen(true);
    setAnchorEl(null);
  };

  const handleCloseFotoLoeschenBestaetigungsdialog = () => {
    setFotoLoeschenBestaetigungsdialogOpen(false);
  };

  const handleOpenBildbearbeitenModal = (index: number) => {
    setBildbearbeitenModalOpen(index);
    setAnchorEl(null);
  };

  const handleDelete = () => {
    setFotoLoeschenBestaetigungsdialogOpen(false);
    onDelete([foto]);
  };

  const handleOpenTexterkennungModal = () => {
    setTexterkennungModalOpen(true);
    setAnchorEl(null);
  };

  const handleCloseTexterkennungModal = () => {
    setTexterkennungModalOpen(false);
  };

  const handleIdentifyText = (zugeordneteFelder: Feld[], zugeordneteKategorien: TexterkennungKategorie[], fotoType: FotoType) => {
    setTexterkennungModalOpen(false);

    const fotos = (vorgang.fotos ?? []).map(fuegeKategorienZuFotoHinzu(fotoType, zugeordneteKategorien));
    const zuAktualisierenderVorgang: Partial<Vorgang> = { id: vorgang.id, fotos };

    for (const zugeordnetesFeld of zugeordneteFelder) {
      /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
      const vorgangRecord = zuAktualisierenderVorgang as Record<string, Record<string, unknown>>;
      if (!vorgangRecord[zugeordnetesFeld.objekt]) {
        // @ts-expect-error Dynamischer Feldzugriff auf Vorgang über Texterkennungs-Mapping
        vorgangRecord[zugeordnetesFeld.objekt] = vorgang[zugeordnetesFeld.objekt];
      }
      vorgangRecord[zugeordnetesFeld.objekt][zugeordnetesFeld.feld] = zugeordnetesFeld.value;
    }

    speichereVorgang({ ...zuAktualisierenderVorgang });
  };

  const handleOpenUpdateKategorienDialog = () => {
    setKategorienEditierenDialogOpen(true);
    setAnchorEl(null);
  };

  const handleOpenDateiInfoDialog = () => {
    setDateiInfoDialogOpen(true);
    setAnchorEl(null);
  };

  const handleUpdateKategorien = (kategorien: string[]) => {
    setKategorienEditierenDialogOpen(false);
    setAnchorEl(null);

    handleKategorienUmschalten(foto, kategorien);
  };

  const handleChangeBeschreibung = (value: string) => {
    editBeschreibung(foto, value);
  };

  const kategorien = FOTOKATEGORIEN.filter((kategorie) => {
    return !(
      (foto.kategorien ?? []).includes(kategorie) ||
      (kategorie === FOTOKATEGORIEN[1] && // Angebotsanfrage
        !(foto.kategorien ?? []).includes(FOTOKATEGORIEN[0]))
    );
  });
  const beschreibung =
    'Bitte wählen Sie eine Kategorie aus, die dem Foto hinzugefügt werden soll. Bilder mit der Kategorie "Gutachtenrelevant" tauchen im Gutachten auf. Die Kategorie "Angebotsanfrage" kann nur auf Fotos, die bereits die Kategorie "Gutachtenrelevant" haben, vergeben werden.';

  const handleKategorieCheckbox = (k: string[]) => {
    handleKategorienUmschalten(foto, k);
  };

  const handleGutachtenrelevantChecked = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      if (foto.kategorien?.includes('Angebotsanfrage')) {
        handleKategorieCheckbox([...(foto.kategorien ?? []), 'Gutachtenrelevant']);
      } else {
        handleKategorieCheckbox([...(foto.kategorien ?? []), 'Gutachtenrelevant', 'Angebotsanfrage']);
      }
    } else {
      handleKategorieCheckbox(foto.kategorien?.filter((k) => k !== 'Gutachtenrelevant') ?? []);
    }
  };

  const handleAngebotsanfrageChecked = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      handleKategorieCheckbox([...(foto.kategorien ?? []), 'Angebotsanfrage']);
    } else {
      handleKategorieCheckbox(foto.kategorien?.filter((k) => k !== 'Angebotsanfrage') ?? []);
    }
  };

  return (
    <Card
      className={`${foto.kategorien?.includes(FOTOKATEGORIEN[0]) ? 'foto--gutachtenrelevant' : ''}`}
      sx={{
        position: 'relative',
        maxWidth: '100%',
        width: '320px'
      }}
    >
      <>
        {kategorieAuswahl ? (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={foto?.kategorien?.includes('Gutachtenrelevant') ?? false}
                  onChange={handleGutachtenrelevantChecked}
                  name="Gutachtenrelevant"
                  color="primary"
                />
              }
              label="Gutachtenrelevant"
            />
            <br />
            <FormControlLabel
              control={
                <Checkbox
                  checked={foto?.kategorien?.includes('Angebotsanfrage') ?? false}
                  onChange={handleAngebotsanfrageChecked}
                  name="Angebotsanfrage"
                  color="primary"
                />
              }
              label="Angebotsanfrage"
            />
          </>
        ) : showBeschreibungEdit ? (
          <TextField
            variant="standard"
            fullWidth
            label="Beschreibung"
            multiline
            maxRows={4}
            value={foto.beschreibung}
            onChange={(event) => handleChangeBeschreibung(event.target.value)}
          />
        ) : (
          <Box component="div" sx={{ display: 'flex' }} data-testid={foto.dateiname}>
            {(foto.kategorien ?? []).length > 0 && (
              <div className="chip__container">
                {(foto.kategorien?.length ?? 0) > 0 && (
                  <Chip
                    label={`Kategorien: ${foto.kategorien?.length ?? 0}`}
                    color="primary"
                    size="small"
                    onClick={() => setKategorienEditierenDialogOpen(true)}
                    disabled={isDisabled}
                  />
                )}
              </div>
            )}
            <IconButton sx={{ marginLeft: 'auto' }} aria-label="settings" onClick={handleClick} disableRipple disableFocusRipple size="large">
              <MoreVertIcon />
            </IconButton>
          </Box>
        )}
      </>

      <CardActionArea onClick={() => (!isDisabled ? handleOpenBildbearbeitenModal(fotoIndex) : '')}>
        {foto.beschreibung && foto.beschreibung !== '' && (
          <Tooltip
            sx={{
              position: 'absolute',
              top: 0,
              display: 'flex',
              justifyContent: 'left'
            }}
            title={foto.beschreibung}
          >
            <CommentIcon />
          </Tooltip>
        )}

        <CardMedia
          sx={{
            height: 0,
            paddingTop: '56.25%' // 16:9
          }}
          image={blob}
        >
          {!blob && <CircularProgress />}
        </CardMedia>
      </CardActionArea>

      <FotoMenu
        anchorEl={anchorEl}
        onClose={handleMenuClose}
        onOpenFotoLoeschenBestaetigungsdialog={handleOpenFotoLoeschenBestaetigungsdialog}
        onOpenTexterkennungModal={handleOpenTexterkennungModal}
        url={url ?? ''}
        onOpenKategorienEditierenDialog={handleOpenUpdateKategorienDialog}
        onOpenDateiInfoDialog={handleOpenDateiInfoDialog}
        disableAddKategorie={kategorien.length === 0}
        isDisabled={isDisabled}
      />
      {fotoLoeschenBestaetigungsdialogOpen && (
        <FotoLoeschenBestaetigungsdialog
          open={fotoLoeschenBestaetigungsdialogOpen}
          onClose={handleCloseFotoLoeschenBestaetigungsdialog}
          onDelete={handleDelete}
        />
      )}
      {
        <LazyTexterkennungModal
          open={texterkennungModalOpen}
          onClose={handleCloseTexterkennungModal}
          onIdentifyText={handleIdentifyText}
          isLoading={isLoading}
          vorgang={vorgang}
          foto={foto}
          url={url ?? ''}
          setFahrzeughalter={setFahrzeughalter}
          fahrzeughalter={fahrzeughalter}
          setShowPersonEingabeMaske={setShowPersonEingabeMaske}
        />
      }
      {kategorienEditierenDialogOpen && (
        <FotoKategorienEditierenDialog
          open={kategorienEditierenDialogOpen}
          onClose={() => setKategorienEditierenDialogOpen(false)}
          onEdit={handleUpdateKategorien}
          fotoKategorien={foto.kategorien ?? []}
          text={beschreibung}
          titel="Kategorien bearbeiten"
          saveButtonText="Speichern"
        />
      )}
      {dateiInfoDialogOpen && (
        <InfoDialog title="Datei Information" open={dateiInfoDialogOpen} onClose={() => setDateiInfoDialogOpen(false)} text="">
          <div>
            <b>Dateiname:</b>
            {foto.dateiname}
            <br />
            <b>UploadDatum:</b>
            {toDateTimeString(foto.uploaddatum)}
          </div>
        </InfoDialog>
      )}
    </Card>
  );
}

type FotoMenuProps = {
  readonly anchorEl: (EventTarget & HTMLButtonElement) | null;
  readonly onClose: () => void;
  readonly onOpenFotoLoeschenBestaetigungsdialog: () => void;
  readonly onOpenKategorienEditierenDialog: () => void;
  readonly onOpenTexterkennungModal: () => void;
  readonly onOpenDateiInfoDialog: () => void;
  readonly url: string;
  readonly disableAddKategorie: boolean;
  readonly isDisabled: boolean;
};

function FotoMenu({
  anchorEl,
  onClose,
  onOpenFotoLoeschenBestaetigungsdialog,
  onOpenKategorienEditierenDialog,
  onOpenTexterkennungModal,
  onOpenDateiInfoDialog,
  url,
  disableAddKategorie,
  isDisabled
}: FotoMenuProps): JSX.Element {
  return (
    <Menu
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center'
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center'
      }}
      anchorEl={anchorEl}
      keepMounted
      open={Boolean(anchorEl)}
      onClose={onClose}
    >
      <MenuItem onClick={onOpenDateiInfoDialog}>
        <ListItemIcon>
          <InfoIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Datei Information" />
      </MenuItem>
      <MenuItem disabled={disableAddKategorie || isDisabled} onClick={onOpenKategorienEditierenDialog}>
        <ListItemIcon>
          <AddIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Kategorien bearbeiten" />
      </MenuItem>
      <MenuItem onClick={onOpenTexterkennungModal} disabled={isDisabled}>
        <ListItemIcon>
          <ImageSearchIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Text erkennen" />
      </MenuItem>
      <MenuItem onClick={() => window.open(url, '_blank')}>
        <ListItemIcon>
          <ImageSearchIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Bild anzeigen" />
      </MenuItem>
      <MenuItem onClick={onOpenFotoLoeschenBestaetigungsdialog} disabled={isDisabled}>
        <ListItemIcon>
          <DeleteIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Löschen" />
      </MenuItem>
    </Menu>
  );
}

export function fuegeKategorienZuFotoHinzu(foto: FotoType, zugeordneteKategorien: TexterkennungKategorie[]) {
  return (datei: Datei) => {
    if (datei.key === foto.key) {
      const array = [];
      array.push(...(datei.kategorien ?? []));
      array.push(...zugeordneteKategorien.map((kategorie) => kategorie.kategorie));
      const uniqueArray = [...new Set(array)];

      return {
        ...datei,
        kategorien: uniqueArray
      };
    }
    return datei;
  };
}
